<template>
  <div class="admin-layout">
    <!-- Overlay untuk mobile -->
    <div 
      class="sidebar-overlay" 
      :class="{ 'is-visible': isSidebarOpen }"
      @click="toggleSidebar"
    ></div>

    <aside class="sidebar" :class="{ 'is-open': isSidebarOpen }">
      <div class="sidebar-header">
        <h3>Admin Panel</h3>
        <!-- Tombol Close untuk mobile -->
        <button class="close-sidebar-btn" @click="toggleSidebar">&times;</button>
      </div>
      <nav class="sidebar-nav">
        <!-- Tampilkan menu ini hanya untuk Superadmin -->
        <template v-if="isSuperadmin">
          <router-link :to="{ name: 'AdminDashboard' }">Paket Tes</router-link>
          <router-link :to="{ name: 'MasterTestManager' }">Master Tes</router-link>
          <router-link :to="{ name: 'InterpretationManager' }">Interpretasi Skor</router-link>
          <router-link :to="{ name: 'PsychogramManager' }">Struktur Psikogram</router-link>
        </template>

        <router-link :to="{ name: 'ResultsDashboard' }">Hasil Tes</router-link>
        
        <!-- ============================================= -->
        <!--        MENU KHUSUS SUPERADMIN / PSIKOLOG        -->
        <!-- ============================================= -->
        <template v-if="isSuperadmin">
          <router-link :to="{ name: 'GeraiManager' }">Manajemen Gerai</router-link>
          <router-link :to="{ name: 'UserManager' }">Manajemen Pengguna</router-link>
        </template>
        <!-- ============================================= -->

        <router-link :to="{ name: 'AdminProfile' }">Profil Saya</router-link>
      </nav>
      <div class="sidebar-footer">
        <button @click="logout">Logout</button>
      </div>
    </aside>
    
    <main class="main-content">
      <button class="hamburger-button" @click="toggleSidebar">
        &#9776;
      </button>
      <router-view></router-view>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth'; // PASTIKAN PATH INI BENAR

const router = useRouter();
const authStore = useAuthStore(); // Panggil store untuk mendapatkan instance-nya

// State untuk sidebar (jika belum ada)
const isSidebarOpen = ref(false);
const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

// Computed property untuk memeriksa peran admin
const isSuperadmin = computed(() => {
  // Ambil data user dari store
  return authStore.user && (authStore.user.role === 'superadmin' || !authStore.user.gerai_id);
});

// Fungsi untuk logout
const logout = () => {
  authStore.logout(); // Gunakan fungsi logout dari store Anda
  router.push({ name: 'AdminLogin' });
};

// Saat komponen dimuat, pastikan data user sudah ada di store
onMounted(() => {
  // Jika data user belum ada, panggil action untuk mengambilnya
  if (!authStore.user) {
    authStore.fetchUser(); // Asumsi ada action 'fetchUser' di store Anda
  }
});
</script>
<style scoped>
.admin-layout {
  display: flex;
  min-height: 100vh;
  /* Tambahkan posisi relatif agar overlay bisa bekerja */
  position: relative; 
}

.sidebar {
  width: 250px;
  background-color: #2c3e50;
  color: #ecf0f1;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  /* Style untuk transisi buka/tutup */
  transition: transform 0.3s ease-in-out;
  z-index: 1001; /* Pastikan di atas overlay */
}

.sidebar-overlay {
  display: none; /* Default sembunyi */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000; /* Di bawah sidebar, di atas konten */
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  pointer-events: none; /* Jangan block klik kalau tidak visible */
}

.sidebar-overlay.is-visible {
  opacity: 1;
  pointer-events: auto;
}

.main-content {
  flex-grow: 1;
  padding: 20px; /* Sedikit kurangi padding di mobile */
  background-color: #f4f7f6;
  overflow-x: hidden; /* Mencegah scroll horizontal */
}

.hamburger-button {
  display: none; /* Sembunyikan di desktop */
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  margin-bottom: 15px;
}

.close-sidebar-btn {
  display: none; /* Sembunyikan di desktop */
  background: none;
  border: none;
  color: #ecf0f1;
  font-size: 24px;
  cursor: pointer;
  position: absolute;
  top: 15px;
  right: 15px;
}

/* --- MEDIA QUERY UNTUK LAYAR KECIL (misal, di bawah 768px) --- */
@media (max-width: 768px) {
  .sidebar {
    /* Posisikan sidebar di luar layar ke kiri */
    position: fixed; /* Ubah ke fixed biar nggak scroll ikut konten */
    top: 0;
    left: 0;
    bottom: 0;
    transform: translateX(-100%);
  }

  .sidebar.is-open {
    /* Tampilkan sidebar saat 'is-open' aktif */
    transform: translateX(0);
  }
  
  .hamburger-button {
    display: block; /* Tampilkan tombol hamburger di mobile */
  }

  .sidebar-overlay {
    display: block; /* Tampilkan elemen overlay di mobile */
  }

  .close-sidebar-btn {
    display: block; /* Tampilkan tombol close */
  }
  
  .sidebar-header {
    position: relative; /* Untuk positioning tombol close */
    display: flex;
    justify-content: center; /* Center title */
    align-items: center;
  }
}


/* --- Sisa Style (tidak banyak berubah) --- */
.sidebar-header {
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid #34495e;
}
.sidebar-nav {
  flex-grow: 1;
  margin-top: 20px;
}
.sidebar-nav a {
  display: block;
  padding: 15px 20px;
  color: #ecf0f1;
  text-decoration: none;
  transition: background-color 0.2s;
}
.sidebar-nav a:hover {
  background-color: #34495e;
}
.sidebar-nav a.router-link-exact-active {
  background-color: #1abc9c;
  font-weight: bold;
}
.sidebar-footer {
  padding: 20px;
  border-top: 1px solid #34495e;
}
.sidebar-footer button {
  width: 100%;
  padding: 10px;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>